import { motion, AnimatePresence } from 'framer-motion';
import { Phone, Loader2 } from 'lucide-react';
import SpeakingGlow from './SpeakingGlow.jsx';
import ThinkingWave from './ThinkingWave.jsx';

/**
 * CallOrb — admin-console preview-call center visual
 *
 * state → visual:
 *   idle          靜態 ring + phone icon
 *   connecting    旋轉 Loader
 *   listening     pulse breathing (subtle)
 *   user_speaking accent ripple 3 圈
 *   thinking      ThinkingWave 在下方 + 靜止 orb
 *   ai_speaking   SpeakingGlow surround
 *   ended/error   fade out + 灰
 */
export function CallOrb({ state, avatarName }) {
    const isSpeaking = state === 'ai_speaking';
    const isUserSpeaking = state === 'user_speaking';
    const isThinking = state === 'thinking';
    const isListening = state === 'listening';
    const isConnecting = state === 'connecting';
    const isEnded = state === 'ended' || state === 'error';

    const initials = (avatarName || 'AI').trim().slice(0, 2);

    return (
        <div className="relative w-full flex flex-col items-center justify-center gap-3">
            {/* Glow / Ripple layer (positioned behind orb) */}
            <div className="relative w-[260px] h-[260px] flex items-center justify-center">
                {/* AI speaking glow — sits behind */}
                <div className="absolute inset-0 pointer-events-none">
                    <SpeakingGlow isTalking={isSpeaking} />
                </div>

                {/* User speaking ripples — 3 layered concentric circles */}
                <AnimatePresence>
                    {isUserSpeaking && (
                        <>
                            {[0, 0.4, 0.8].map((delay, i) => (
                                <motion.div
                                    key={`ripple-${i}`}
                                    className="absolute rounded-full border border-accent-500"
                                    initial={{ width: 180, height: 180, opacity: 0.6 }}
                                    animate={{
                                        width: 260,
                                        height: 260,
                                        opacity: 0,
                                    }}
                                    exit={{ opacity: 0 }}
                                    transition={{
                                        duration: 1.4,
                                        delay,
                                        repeat: Infinity,
                                        ease: 'easeOut',
                                    }}
                                />
                            ))}
                        </>
                    )}
                </AnimatePresence>

                {/* Listening subtle breath */}
                <AnimatePresence>
                    {isListening && (
                        <motion.div
                            key="breath"
                            className="absolute rounded-full bg-accent-100/30"
                            initial={{ width: 180, height: 180, opacity: 0 }}
                            animate={{
                                width: [180, 200, 180],
                                height: [180, 200, 180],
                                opacity: [0.2, 0.5, 0.2],
                            }}
                            exit={{ opacity: 0 }}
                            transition={{
                                duration: 3.6,
                                repeat: Infinity,
                                ease: 'easeInOut',
                            }}
                        />
                    )}
                </AnimatePresence>

                {/* Orb core */}
                <motion.div
                    className={[
                        'relative w-[180px] h-[180px] rounded-full flex items-center justify-center',
                        'shadow-[0_8px_30px_rgba(60,40,20,0.18),inset_0_2px_4px_rgba(255,255,255,0.6)]',
                        'transition-colors duration-500',
                    ].join(' ')}
                    style={{
                        background: isEnded
                            ? 'linear-gradient(135deg, #e6dccc 0%, #c8b794 100%)'
                            : 'linear-gradient(135deg, #fdf8ed 0%, #efe0c5 50%, #d9b685 100%)',
                    }}
                    animate={{
                        scale: isSpeaking ? [1, 1.04, 1] : 1,
                    }}
                    transition={{
                        scale: {
                            duration: 0.8,
                            repeat: isSpeaking ? Infinity : 0,
                            ease: 'easeInOut',
                        },
                    }}
                >
                    {isConnecting ? (
                        <Loader2 size={42} strokeWidth={1.5} className="text-accent-700 animate-spin" />
                    ) : isEnded ? (
                        <Phone size={42} strokeWidth={1.5} className="text-ink-400" />
                    ) : (
                        <div className="flex flex-col items-center gap-1.5">
                            <span
                                className="heading-display text-3xl font-medium text-ink-800 tracking-wide"
                                style={{ fontFeatureSettings: '"ss01"' }}
                            >
                                {initials}
                            </span>
                            <div
                                className={[
                                    'h-1.5 w-1.5 rounded-full transition-all',
                                    isSpeaking ? 'bg-accent-700 scale-100' : 'bg-accent-400 scale-75 opacity-60',
                                ].join(' ')}
                            />
                        </div>
                    )}
                </motion.div>
            </div>

            {/* ThinkingWave below orb */}
            <ThinkingWave isActive={isThinking} />
        </div>
    );
}
